import { OnlineStockData, Product, Variant } from "../../types/types";
import { createContext, ReactNode, useContext, useMemo, useState } from "react";
import { getVariant, isValidColour, useOnlineStockPromise } from "./utils";
import { useProductCardMetadata } from "../ProductCardMetadataContext/ProductCardMetadataContext";

type Props = {
  product: Product;
  children: ReactNode;
};

type VariantContextType = {
  variant: Variant | null;
  onlineStock: Promise<OnlineStockData | undefined>;
  setSelectedColour: (colour: string) => void;
};

const VariantContext = createContext<VariantContextType | undefined>(undefined);

export function VariantProvider({
  children,
  product,
}: Props): React.JSX.Element {
  const { config, consumerId, instanceId } = useProductCardMetadata();
  const [userSelectedColour, setUserSelectedColour] = useState<string | null>(
    null,
  );

  const validateAndSetSelectedColour = (colour: string) => {
    if (isValidColour(colour, product)) {
      setUserSelectedColour(colour);
    }
  };

  const variant = useMemo(
    () =>
      getVariant({
        product,
        userSelectedColour,
      }),
    [product, userSelectedColour],
  );

  return (
    <VariantContext.Provider
      value={{
        variant,
        onlineStock: useOnlineStockPromise(
          product,
          variant?.aliases?.skuId,
          config,
          consumerId,
          instanceId,
        ),
        setSelectedColour: validateAndSetSelectedColour,
      }}
    >
      {children}
    </VariantContext.Provider>
  );
}

export function useVariant(): VariantContextType {
  const context = useContext(VariantContext);

  if (!context) {
    throw new Error("useVariant must be used within a VariantProvider");
  }

  return context;
}
